import { faAngleLeft, faComment } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EmbedHTML from "components/shared/embedHTML";
import Tooltip from "components/Tooltip";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import moment from "moment-timezone";
import { useContext, useReducer, useState, useEffect } from "react";
import dateTime, { checkAndGetTimezone } from "utils/helpers/dateTime";
import { priceFormatter, formatPhoneNumber } from "utils/helpers/validation";
import { MobXProviderContext } from "mobx-react";
import { Disclosure } from "@headlessui/react";
import { Transition } from "@headlessui/react";
import ConfirmationModal from "components/ConfirmationModal";
import { getOrderStatus } from "utils/helpers/order";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { getLocationEmployeesWithAdmin } from "../../utils/helpers/array";
import { formattedAddres } from "utils/helpers/string";
import { SearchBar, PageGuide } from "components";

function CateringOrderDetail() {
  const initialState = {
    order: null,
    updateEmailModal: false,
    searchText: "",
  };

  function reducer(state, action) {
    switch (action.type) {
      case "reset":
        return initialState;
    }
    return {
      ...state,
      [action.field]: action.value,
    };
  }

  const [provider, dispatch] = useReducer(reducer, initialState);
  const [cancelOrderAlert, setCancelOrderAlert] = useState(null);
  const store = useContext(MobXProviderContext);
  const userStore = toJS(store?.userStore);
  const { currentUser, defaultLocation } = userStore;
  const navigate = useNavigate();
  const { slug, locationId } = useParams();
  const location = useLocation();

  const { isPending, error, data, refetch } = useQuery({
    queryKey: ["cateringOrder", locationId, slug],
    refetchOnWindowFocus: false,
    queryFn: () => {
      return store.mealManagementStore.fetchCateringOrder(slug, locationId);
    },
  });

  const mutation = useMutation({
    mutationFn: () =>
      store.mealManagementStore?.cancelOrder(order?.attributes?.id),
    onSuccess: (data) => {
      setCancelOrderAlert(null);
      refetch();
    },
    onError: (error) => {
      console.error("Error cancelling order:", error);
    },
  });

  const order = data;
  const [filteredOrder, setFilteredOrder] = useState(data);

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
    });
  }, []);

  useEffect(() => {
    if (!provider.searchText) {
      setFilteredOrder(data);
    } else {
      const searchTerm = provider.searchText?.toLowerCase();

      const filteredCategories = data?.attributes?.menu_categories?.map(
        (category) => ({
          ...category,
          order_items: category?.order_items?.filter((item) =>
            item?.attributes?.menu_item?.attributes?.display_name_with_html
              ?.toLowerCase()
              ?.includes(searchTerm)
          ),
        })
      );

      setFilteredOrder({
        ...order,
        attributes: {
          ...order.attributes,
          menu_categories: filteredCategories,
        },
      });
    }
  }, [provider.searchText, data]);

  // useEffect(() => {
  //   if (currentUser && locationId) {
  //     const locationEmployees = getLocationEmployeesWithAdmin(currentUser);
  //     const preSelectedLocation = locationEmployees?.find(
  //       (employee) => Number(employee.organizationId) === Number(locationId)
  //     );
  //      store.userStore?.setdefaultLocation(preSelectedLocation);
  //   }
  // }, []);

  const getTime = () => {
    if (order?.attributes?.status == "completed")
      return order.attributes.completed_at
        ? `${dateTime.localWithUTCoffset(
            order.attributes.completed_at,
            order.attributes.tzinfo,
            "h:mm A"
          )}  ${checkAndGetTimezone(order.attributes.tzinfo)}`
        : "Not available";
    else
      return `${order.attributes.delivery_time} ${checkAndGetTimezone(
        order.attributes.tzinfo
      )}`;
  };

  const showCancelButton = () => {
    if (order.attributes.status !== "pending") return false;

    if (parseFloat(order.attributes.grand_total).toFixed(2) > 5000)
      return false;

    let dateTime = moment(
      order.attributes.order_date + " " + order.attributes.delivery_time,
      "YYYY-MM-DD hh:mm A"
    );

    let duration = moment.duration(dateTime.diff(moment()));
    let hours = duration.asHours();

    let sortedMinimumOrderPreferences =
      order?.attributes?.minimum_order_preferences?.sort(
        (a, b) => a.minimum_amount - b.minimum_amount
      );

    if (hours < calculateOrderCancellationHours(sortedMinimumOrderPreferences))
      return false;

    return true;
  };

  const calculateOrderCancellationHours = (array) => {
    let targetValue = order?.attributes?.subtotal;

    if (array.length === 0) return 0;

    if (array.length === 1) {
      return array[0]?.required_notice_time;
    }

    if (targetValue > array[array.length - 1].minimum_amount) {
      return array[array.length - 1].required_notice_time;
    }

    for (let i = 0; i < array.length - 1; i++) {
      let currentObj = array[i];
      let nextObj = array[i + 1];

      if (
        targetValue >= currentObj.minimum_amount &&
        targetValue <= nextObj.minimum_amount
      ) {
        return currentObj.required_notice_time;
      }
    }
  };

  const getETA = () => {
    return `${dateTime.localWithUTCoffset(
      order.attributes.dropoff_task_eta,
      order.attributes.tzinfo,
      "h:mm A"
    )} ${dateTime.checkAndGetTimezone(order.attributes.tzinfo)}`;
  };

  const getETAString = () => {
    if (order?.attributes?.dropoff_task_eta) {
      return `Your estimated time of arrival: ${getETA()}`;
    }
  };

  const getCancellationDeadline = () => {
    return moment(order?.attributes?.cancellation_deadline)
      .utc()
      .format("dddd Do MMMM YYYY - hh:mma");
  };

  const onCancelOrder = () => {
    mutation.mutate();
  };

  const itemInstructionsAndName = (item) => (
    <div className="flex flex-col py-2 px-1 rounded-md">
      {item?.attributes?.special_instructions && (
        <div className="flex items-center ">
          <FontAwesomeIcon
            icon={faComment}
            size="1x"
            className="small-icon"
            style={{ color: "#2D3958" }}
          />

          <span className="text-sm ml-2 text-[#2d3958]">
            Instructions:{" "}
            <span className="font-inter-normal text-[#2d3958]">
              {item?.attributes?.special_instructions}
            </span>
          </span>
        </div>
      )}
    </div>
  );

  const orderDetails = (
    <>
      <div className="mt-2">
        <div className="flex py-5 px-3 bg-background">
          <div className="text-sm font-inter-medium w-2/12 hidden sm:flex">
            QTY
          </div>

          <div className="text-sm font-inter-medium w-2/12 flex sm:hidden">
            Q-ty
          </div>

          <div className="text-sm font-inter-medium w-10/12 md:w-4/12">
            Item
          </div>

          <div className="text-sm font-inter-medium w-3/12 text-left">
            Food Total
          </div>

          <div className="text-sm font-inter-medium w-3/12 text-left">
            Name/Label
          </div>
        </div>

        {filteredOrder?.attributes?.menu_categories?.map((category) => (
          <div className="flex flex-col" key={category.id}>
            <Disclosure defaultOpen={true}>
              {({ open }) => (
                /* Use the `open` state to conditionally change the direction of an icon. */
                <>
                  <Transition
                    enter="transition duration-100 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="transition duration-75 ease-out"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0"
                  >
                    <Disclosure.Panel>
                      {category?.order_items?.map((item) => (
                        <div
                          key={item.id}
                          className="flex w-full px-3 py-3 mt-3 border-b border-[#f3f3f3]"
                        >
                          <div className="text-sm w-2/12">
                            <span>{item.attributes.quantity}</span>
                          </div>

                          <div className="text-sm w-10/12 md:w-4/12">
                            <div className="flex flex-col">
                              <span className="text-[14px]">
                                <EmbedHTML
                                  text={
                                    item?.attributes?.menu_item?.attributes
                                      ?.display_name_with_html
                                  }
                                  className="text-header-text-2 text-[14px] font-inter-medium"
                                />
                              </span>

                              {item?.attributes?.quantity > 1 && (
                                <div className="h-4" />
                              )}

                              {item?.attributes?.order_item_options?.map(
                                (option) => (
                                  <div className="flex mt-1" key={option.id}>
                                    <span className="d-row text-sm">
                                      <EmbedHTML
                                        isRow
                                        text={
                                          option?.attributes
                                            ?.option_group_display_name_with_html
                                        }
                                        append={": "}
                                        appendClass="mr-1"
                                        className="text-primary-black text-[12px]"
                                      />
                                      <EmbedHTML
                                        text={
                                          option?.attributes
                                            ?.option_display_name_with_html
                                        }
                                        className="text-primary-black font-inter-normal text-[12px]"
                                      />
                                    </span>
                                  </div>
                                )
                              )}

                              <div className="flex">
                                {itemInstructionsAndName(item)}
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col w-3/12 text-left">
                            <span className="text-black font-inter-normal text-[14px]">
                              {priceFormatter(item?.attributes?.total_price)}
                            </span>

                            {item?.attributes?.quantity > 1 && (
                              <span
                                className="text-sm"
                                style={{ color: "#8F979F" }}
                              >
                                {priceFormatter(
                                  item?.attributes?.menu_item?.attributes?.price
                                )}{" "}
                                per item
                              </span>
                            )}

                            {item?.attributes?.order_item_options?.map(
                              (option) => (
                                <div className="flex mt-1" key={option.id}>
                                  <span
                                    className="text-sm"
                                    style={{ color: "#8F979F" }}
                                  >
                                    {priceFormatter(option?.attributes?.price)}{" "}
                                  </span>
                                </div>
                              )
                            )}
                          </div>

                          <div className="flex flex-col w-3/12 text-left">
                            {item?.attributes?.notes && (
                              <span className="font-inter-normal text-[14px]">
                                {item?.attributes?.notes}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </Disclosure.Panel>
                  </Transition>
                </>
              )}
            </Disclosure>
          </div>
        ))}
      </div>
    </>
  );

  const normalizeAddress = (address) => {
    return address?.trim()?.toLowerCase();
  };

  const isSameAddress =
    normalizeAddress(
      order?.attributes?.delivery_details?.attributes?.complete_address
    ) == normalizeAddress(order?.attributes?.location_billing_account?.address);

  return (
    <>
      <div
        className="flex flex-col p-10 pb-14 bg-white min-h-screen md:pt-8 mx-auto w-full"
        style={{ maxWidth: "1440px" }}
      >
        {order ? (
          <>
            <div className="mb-5">
              <button
                className="flex items-center btn-style"
                onClick={() => {
                  if (location?.state?.from == "/order-history")
                    navigate("/order-history");
                  else navigate("/meal-management");
                }}
              >
                <div className="flex" style={{ width: 16, height: 16 }}>
                  <FontAwesomeIcon
                    icon={faAngleLeft}
                    size="1x"
                    className="icon"
                  />
                </div>

                <span className="text-md font-inter-medium ml-2">
                  Back to Orders
                </span>
              </button>
            </div>

            {order?.attributes?.status !== "completed" && (
              <div className="flex flex-col bg-background rounded-md">
                {showCancelButton() ? (
                  <span className="text-[14px] py-3 px-3 font-inter-regular">
                    Please note that cancellation is only possible until{" "}
                    {getCancellationDeadline()}
                  </span>
                ) : (
                  <>
                    {order?.attributes?.dropoff_task_eta && (
                      <div className="p-[12px] d-row items-center rounded-lg">
                        <span className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-sky-500"></span>
                        </span>

                        <span className="px-3 font-inter-normal text-[16px]">
                          {getETAString()}.
                        </span>

                        <button className="text-catering font-inter-medium text-[16px]">
                          Contact us
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {order?.attributes?.status == "cancelled" && (
              <div className="flex flex-row items-center px-3 bg-background rounded-md">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-100"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-500"></span>
                </span>

                <span className="text-[14px] py-3 pl-3 font-inter-medium">
                  This order has been successfully cancelled.
                </span>
                <button
                  onClick={() => {
                    navigate("/order-history");
                  }}
                  className="text-catering ml-2 font-inter-medium text-[16px]"
                >
                  View all orders
                </button>
              </div>
            )}

            {order?.attributes?.status == "completed" && (
              <div className="flex flex-row items-center px-3 bg-background rounded-md">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-100"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-600"></span>
                </span>

                <span className="text-[14px] py-3 pl-3 font-inter-medium">
                  Your order is complete.
                </span>
              </div>
            )}

            <div className=" d-row w-full my-[32px] justify-between">
              <h1 className="text-dark-gray font-inter-semibold text-[32px]">
                Order Details
              </h1>

              <PageGuide />
            </div>

            <div className="d-col border border-[#DEE2E6] border-1 rounded-lg p-[12px]">
              <div className="flex flex-row justify-between items-center">
                <h1 className="font-inter-semibold text-slate-700 text-[24px] text-[#2B313C]">
                  Your Order From {order?.attributes?.restaurant_name}
                </h1>

                <div>
                  <button
                    disabled={!showCancelButton()}
                    className={`mr-3 btn-outline ${
                      !showCancelButton() && "opacity-20"
                    }`}
                    onClick={() => setCancelOrderAlert(order)}
                  >
                    <span className="text-hh-accent-light text-[15px] font-inter-medium">
                      Cancel Order
                    </span>
                  </button>

                  {order?.attributes?.status !== "cancelled" && (
                    <button
                      className="btn-purple bg-[#575BE8]"
                      onClick={() =>
                        store.mealManagementStore?.generatePDF(
                          order?.id,
                          defaultLocation?.organizationId,
                          order?.attributes?.restaurant_name,
                          order?.attributes?.order_date
                        )
                      }
                    >
                      Download PDF invoice
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 my-[24px] rounded-lg">
                <div className="flex flex-col">
                  <span className="text-[14px] font-inter-semibold text-black-light">
                    Delivery date
                  </span>
                  <span className="text-[14px] font-inter-medium">
                    {dateTime.formatDate(
                      order?.attributes?.order_date,
                      dateTime.longDateWithWeek
                    )}{" "}
                  </span>
                </div>

                <div className="flex flex-col">
                  <div className="d-row">
                    <span className="text-[14px] font-inter-semibold text-black-light">
                      Delivery window
                    </span>
                    <Tooltip
                      grayIcon
                      tooltipText={
                        "Delivery windows are set to allow for 15 minutes before and after your requested delivery time."
                      }
                      id="delivery-tooltip-time"
                      backgroundColor={"white"}
                      textColor="black"
                    />
                  </div>

                  <span className="font-inter-medium text-[14px] text-black-light mt-1 overflow-hidden overflow-ellipsis">
                    {dateTime.getDeliveryWindow(
                      order?.attributes.delivery_time,
                      order?.attributes?.order_date
                    )}
                  </span>
                </div>

                <div className="flex flex-col">
                  <span className="text-[14px] font-inter-semibold text-black-light">
                    Point of contact
                  </span>

                  <span className="text-[16px] font-inter-medium">
                    {order.attributes?.delivery_details?.attributes
                      ?.delivery_contact_name ?? "" + " "}
                  </span>

                  <span className="text-[14px] font-inter-medium">
                    {formatPhoneNumber(
                      order.attributes?.delivery_details?.attributes
                        ?.delivery_contact_phone
                    )}
                  </span>
                </div>

                <div className="flex flex-col">
                  <span className="text-[14px] font-inter-semibold text-black-light">
                    Delivery address
                  </span>
                  <span className="text-[14px] font-inter-medium">
                    {formattedAddres(
                      order?.attributes?.delivery_details?.attributes
                    )}
                  </span>
                </div>

                {!isSameAddress && (
                  <div className="flex flex-col">
                    <span className="text-[14px] font-inter-semibold text-black-light">
                      Billing address
                    </span>
                    <span className="text-[14px] font-inter-medium">
                      {order.attributes?.location_billing_account?.address ??
                        ""}
                    </span>
                  </div>
                )}

                <div className="flex flex-col">
                  <span className="text-[14px] font-inter-semibold text-black-light">
                    Delivery instructions
                  </span>
                  <span className="text-[14px] font-inter-medium">
                    {order?.attributes?.permanent_instructions ??
                      order?.attributes?.delivery_details?.attributes
                        ?.delivery_instructions}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex mt-[32px] flex-col md:flex-row md:items-start rounded-3xl px-2 sm:px-5 md:px-0 md:border-0">
              <div className="flex flex-col w-full md:w-3/5 md:rounded-3xl md:mr-3">
                <SearchBar
                  width="w-full mb-[12px]"
                  className="w-full"
                  placeholder="Search"
                  value={provider.searchText}
                  handleOnChange={(e) =>
                    dispatch({ field: "searchText", value: e.target.value })
                  }
                />

                <div className="hidden md:flex flex-col">{orderDetails}</div>
              </div>

              <div className="flex flex-col w-full md:w-2/5 mt-5 md:mt-0 p-5 py-7 border border-[#DEE2E6] rounded-3xl">
                <div className="text-md2 d-row items-center font-inter-medium mt-5">
                  {order?.attributes?.restaurant_name}
                </div>

                <div className="mt-3">
                  <div className="flex w-full items-center justify-between mb-2">
                    <span className="text-md">Food Total</span>

                    <span className="text-md">
                      {priceFormatter(order?.attributes?.subtotal)}
                    </span>
                  </div>

                  <div className="flex w-full items-center justify-between mb-2">
                    <span className="text-md">Tax</span>

                    <span className="text-md">
                      {priceFormatter(order?.attributes?.hst)}
                    </span>
                  </div>

                  <div className="flex w-full items-center justify-between mb-2">
                    <div className="flex items-center">
                      <span className="text-[16px] font-inter-normal">
                        Service Fee
                      </span>
                      <Tooltip
                        grayIcon
                        tooltipText={
                          "A service fee of seven percent of food total charges is inclusive of all hungerhub fees"
                        }
                        id="service-tooltip-order"
                        backgroundColor={"white"}
                        textColor="black"
                        place={"bottom"}
                      />
                    </div>

                    <span className="text-md">
                      {priceFormatter(order?.attributes?.service_fee)}
                    </span>
                  </div>

                  <div className="flex w-full items-center justify-between mb-2">
                    <div className="flex items-center">
                      <span className="text-md">Delivery</span>
                      <Tooltip
                        grayIcon
                        tooltipText={
                          "Your delivery cost is dependent on restaurant and proximity to your delivery address"
                        }
                        id="delivery-tooltip-order"
                        backgroundColor={"white"}
                        textColor="black"
                        place={"bottom"}
                      />
                    </div>

                    <span className="text-md">
                      {priceFormatter(order?.attributes?.delivery_fee)}
                    </span>
                  </div>

                  <div className="flex w-full items-center justify-between mb-2">
                    <span className="text-md">Tip</span>

                    <span className="text-md">
                      {priceFormatter(order?.attributes?.tip)}
                    </span>
                  </div>

                  <hr className="my-5 mb-5" />

                  <div className="flex w-full items-center justify-between">
                    <span className="text-md">Total</span>

                    <span className="text-md">
                      {priceFormatter(order?.attributes?.grand_total)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="sm:flex flex-col my-4 md:hidden">
                {orderDetails}
              </div>

              <ConfirmationModal
                visible={cancelOrderAlert}
                setModal={setCancelOrderAlert}
                title={"Order cancellation"}
                body={`Are you sure you want to cancel this order?`}
                rightButtonText={"Cancel"}
                onClickRightButton={onCancelOrder}
                leftButtonText={"Go Back"}
              />
            </div>
          </>
        ) : (
          !isPending && (
            <span className="my-5 text-center text-md">
              Order doesn't exists
            </span>
          )
        )}
      </div>
    </>
  );
}

export default observer(CateringOrderDetail);
